/**
 * Retries a failed image on the production space before anything shows a
 * placeholder.
 *
 * Staging runs on a copy of production data, so a row can carry an image url on
 * `traveladev.…` while the file itself only lives in the `travelaprod` bucket.
 * That request 404s and the image component drops to its placeholder, which
 * reads as "this listing has no photo" rather than "this url moved".
 *
 * One capture-phase listener instead of an `onError` in each of the ~30 places
 * an image is rendered: `error` does not bubble, so a capture listener on
 * `window` is the only spot that sees every failure, `next/image` included. It
 * runs before React's own handler and stops the event there, so a component's
 * placeholder still appears — but only once the production url has failed too.
 */

const DEV_HOST = "traveladev.sgp1";
const PROD_HOST = "travelaprod.sgp1";

/**
 * The same file on the production space. Anything else comes back untouched.
 *
 * Works on an optimizer url as well (`/_next/image?url=…`): the encoding leaves
 * the host itself literal.
 */
const toProdCdn = (url: string): string => url.split(DEV_HOST).join(PROD_HOST);

/** Points one already-failed `<img>` at the production space. */
const retryOnProd = (img: HTMLImageElement): boolean => {
  const src = img.getAttribute("src") ?? "";
  if (!src.includes(DEV_HOST)) return false;

  // `srcset` wins over `src` wherever the browser has one, so it has to move
  // across as well or the retry loads the dev url again.
  const srcset = img.getAttribute("srcset");
  if (srcset?.includes(DEV_HOST)) img.setAttribute("srcset", toProdCdn(srcset));

  img.setAttribute("src", toProdCdn(src));
  return true;
};

/** Call once on mount. Returns the listener's own cleanup. */
export const installImageCdnFallback = (): (() => void) => {
  if (typeof window === "undefined") return () => {};

  const onError = (event: Event) => {
    const target = event.target;
    if (target instanceof HTMLImageElement && retryOnProd(target)) {
      // Swallowed only when there is something left to try: the second failure
      // carries no dev host, so it reaches the component and the placeholder.
      event.stopPropagation();
    }
  };

  window.addEventListener("error", onError, true);

  // Server-rendered markup starts loading long before hydration, so an image
  // that failed early has already fired its event at nobody.
  document.querySelectorAll("img").forEach((img) => {
    if (img.complete && img.naturalWidth === 0) retryOnProd(img);
  });

  return () => window.removeEventListener("error", onError, true);
};
